import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Chip,
  Divider
} from '@heroui/react';
import { CreditCardIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

interface PaymentMethod {
  id: number;
  type: string;
  lastFour: string;
  expiration: string;
  isDefault: boolean;
}

interface InvoiceItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface InvoiceDetails {
  id: string;
  date: string;
  amount: string;
  status: string;
  plan: string;
  items: InvoiceItem[];
}

interface PaymentRetryModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceDetails | null;
  paymentMethods: PaymentMethod[];
  onPaymentRetry: (paymentMethodId: number) => void;
}

const PaymentRetryModal: React.FC<PaymentRetryModalProps> = ({ 
  isOpen, 
  onClose, 
  invoice, 
  paymentMethods,
  onPaymentRetry
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  
  if (!invoice) return null;

  const handlePaymentRetry = () => {
    if (selectedPaymentMethod !== null) {
      onPaymentRetry(Number(selectedPaymentMethod));
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="2xl"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <CurrencyDollarIcon className="w-6 h-6 text-danger" />
                <h3 className="text-xl font-semibold">Reintentar Pago</h3>
              </div>
              <div className="text-sm text-gray-500">
                Factura: {invoice.id} | Estado: {invoice.status}
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                {/* Payment Method Selection */}
                <div>
                  <h4 className="text-md font-medium mb-2">Método de Pago</h4>
                  <Select
                    label="Selecciona un método de pago"
                    placeholder="Elige un método de pago"
                    selectedKeys={selectedPaymentMethod ? [selectedPaymentMethod] : []}
                    className="max-w-xs"
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys).join('');
                      setSelectedPaymentMethod(value || null);
                    }}
                  >
                    {paymentMethods.map((method) => (
                      <SelectItem 
                        key={method.id.toString()} 
                      >
                        {method.type} terminado en {method.lastFour} 
                        {method.isDefault && ' (Predeterminado)'}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                {/* Payment Amount */}
                <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-100/10">
                  <h4 className="text-md font-medium mb-2">Valor del Pago</h4>
                  <p className="text-2xl font-bold text-primary">{invoice.amount}</p>
                </div>

                {/* Payment Details */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-md font-semibold">Detalle del Pago</h4>
                    <Chip 
                      color="danger"
                      variant="flat"
                      size="sm"
                    >
                      {invoice.status === 'failed' ? 'Fallido' : 'Pagado'}
                    </Chip>
                  </div>

                  <div className="space-y-4">
                    {invoice.items.map((item, index) => (
                      <div key={index} className="border-b pb-3 last:border-0 last:pb-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium">{item.name}</h5>
                            <p className="text-sm text-gray-500">
                              {item.quantity} unidades × ${item.unitPrice.toLocaleString('es-CO')} COP
                            </p>
                          </div>
                          <p className="font-semibold">
                            ${item.total.toLocaleString('es-CO')} COP
                          </p>
                        </div>
                        
                        {/* Detailed explanation of the charge */}
                        {item.name.includes('usuarios') && (
                          <div className="mt-2 text-sm text-gray-600">
                            <p>Detalle del cargo:</p>
                            <ul className="list-disc pl-5 space-y-1 mt-1">
                              <li>{item.quantity} usuarios registrados</li>
                              <li>Valor adicional por usuario: {'$' + (10000).toLocaleString('es-CO') + ' COP'}</li>
                              <li>Valor total adicional: {'$' + item.total.toLocaleString('es-CO') + ' COP'}</li>
                            </ul>
                          </div>
                        )}
                        
                        {item.name.includes('documentos') && (
                          <div className="mt-2 text-sm text-gray-600">
                            <p>Detalle del cargo:</p>
                            <ul className="list-disc pl-5 space-y-1 mt-1">
                              <li>{item.quantity} documentos</li>
                              <li>Valor adicional por documento: {'$' + (500).toLocaleString('es-CO') + ' COP'}</li>
                              <li>Valor total adicional: {'$' + item.total.toLocaleString('es-CO') + ' COP'}</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <Divider className="my-4" />
                  
                  <div className="flex justify-end">
                    <div className="w-48">
                      <div className="flex justify-between py-2">
                        <span className="text-gray-500">Subtotal:</span>
                        <span>
                          ${(invoice.items.reduce((sum, item) => sum + item.total, 0)).toLocaleString('es-CO')} COP
                        </span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-500">IVA (0%):</span>
                        <span>$0 COP</span>
                      </div>
                      <Divider />
                      <div className="flex justify-between py-2 font-bold text-lg">
                        <span>Total:</span>
                        <span className="text-primary">{invoice.amount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="default" variant="light" onClick={onClose}>
                Cancelar
              </Button>
              <Button 
                color="danger" 
                startContent={<CreditCardIcon className="w-4 h-4" />}
                onClick={handlePaymentRetry}
                disabled={!selectedPaymentMethod}
              >
                Reintentar Pago
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PaymentRetryModal;